import type { TFunction } from "i18next";
import {
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
  EMPLOYEE_CATEGORY_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  SALARY_OPTIONS,
} from "model/OrganizationalStructure";
import { SalaryType } from "model/Salary";
import { isSettingEnabled } from "model/settings.logic";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { Api } from "services/api/api.service";
import type { BackendAxiosError } from "services/api/backendError";
import { getBackendErrorMessage } from "services/api/backendError";
import type {
  EmploymentTemplateListItem,
  EmploymentTemplateSalary,
} from "services/api/company/staff.service";
import { useCompanySubscription } from "web-apps/company/components/common/hooks/useCompanySubscription";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type {
  AddStaffFormData,
  AddStaffOption,
  AddStaffValidationRequirements,
  AddStaffValidationState,
} from "./addStaffPanel.contract";
import {
  applyEmploymentTemplateToAddStaffForm,
  createInitialFormData,
  mapAddStaffFormDataToWorkerPayload,
} from "./addStaffPanel.mapper";
import {
  toEmploymentTypeOptions,
  toTranslatedOptions,
} from "../shared/staffPanel.options.logic";
import { filterTemplateSalariesByAge } from "../shared/staffEmploymentTemplate.logic";
import { getAgeFromPersonalIdentityNumber } from "../shared/staffPersonalIdentityNumber.logic";
import {
  getAddStaffValidationRequirements,
  getAddStaffValidationState,
  isValidPhoneNumber,
} from "./addStaffPanel.validator";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import {
  PAYMENT_METHOD_OPTIONS,
  toPaymentMethodFallbackErrorTranslationKey,
  validatePaymentMethodValue,
  toPaymentMethodValidationTranslationKey,
} from "../shared/staff.paymentMethod.logic";

interface UseAddStaffPanelLogicProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkerCreated?: () => void;
  unitTeamOptions: UnitTeamOption[];
  t: TFunction;
}

interface AddStaffPanelLogicResult {
  formData: AddStaffFormData;
  isLoadingEmployeeNumber: boolean;
  isCheckingPhoneNumber: boolean;
  isSubmitting: boolean;
  phoneError: string;
  submitError: string;
  teamOptions: AddStaffOption[];
  employmentOptions: AddStaffOption[];
  salaryOptions: AddStaffOption[];
  paymentOptions: AddStaffOption[];
  paymentMethodOptions: AddStaffOption[];
  paymentMethodError: string;
  employeeCategoryOptions: AddStaffOption[];
  salaryAmountLabel: string;
  areFormFieldsDisabled: boolean;
  isCreateDisabled: boolean;
  validationRequirements: AddStaffValidationRequirements;
  validationState: AddStaffValidationState;
  employmentTemplates: EmploymentTemplateListItem[];
  isLoadingEmploymentTemplates: boolean;
  selectedEmploymentTemplateId: string;
  applyEmploymentTemplate: (templateId: string) => void;
  templateSalaries: EmploymentTemplateSalary[];
  selectedTemplateSalaryId: string;
  applyEmploymentTemplateSalary: (salaryId: string) => void;
  fetchEmployeeNumber: () => void;
  setField: <K extends keyof AddStaffFormData>(
    field: K,
    value: AddStaffFormData[K],
  ) => void;
  onPhoneNumberChange: (phoneNumber: string) => void;
  onPhoneNumberBlur: () => void;
  onPhoneNumberContinue: () => void;
  noticeDaysInputController: NumericFieldInputController;
  vacationDaysInputController: NumericFieldInputController;
  onAddWorkerClick: () => void;
}

interface NumericFieldInputController {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}

type PhoneLookupState =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "invalid"; message: string }
  | { status: "alreadyExists"; message: string }
  | { status: "verified" };

type SubmitState = { status: "idle" } | { status: "submitting" };

const SIMFLEXITY_PAY_SETTING_KEY = "SimflexityPay";
const MAX_PHONE_DIGITS = 15;
const ADD_STAFF_BACKEND_MESSAGE_TO_TRANSLATION_KEY: Record<string, string> = {
  "Employee number is already in use":
    "Staff.ProfilePanelErrorEmployeeNumberAlreadyInUse",
  "Invalid payment method type": "Staff.PaymentMethodTypeInvalid",
};

const getPaymentMethodError = (
  formData: AddStaffFormData,
  isValidPaymentMethod: boolean,
  translate: TFunction,
): string => {
  if (isValidPaymentMethod) return "";
  const validationResult = validatePaymentMethodValue(
    formData.paymentMethodType,
    formData.paymentMethodValue,
  );
  if (validationResult.type === "success") return "";
  return translate(
    toPaymentMethodValidationTranslationKey(validationResult.error),
  );
};

const resolveCreateWorkerErrorMessage = (
  error: BackendAxiosError,
  formData: AddStaffFormData,
  translate: TFunction,
): string => {
  const backendMessage = getBackendErrorMessage(error);
  if (!backendMessage) {
    return translate("Alerts.SomethingWentWrong");
  }

  if (backendMessage === "Invalid payment method value") {
    const validationResult = validatePaymentMethodValue(
      formData.paymentMethodType,
      formData.paymentMethodValue,
    );

    if (validationResult.type === "failure") {
      return translate(
        toPaymentMethodValidationTranslationKey(validationResult.error),
      );
    }

    return translate(
      toPaymentMethodFallbackErrorTranslationKey(formData.paymentMethodType),
    );
  }

  const mappedKey =
    ADD_STAFF_BACKEND_MESSAGE_TO_TRANSLATION_KEY[backendMessage];
  if (mappedKey) return translate(mappedKey);

  return translate("Alerts.SomethingWentWrong");
};

const getSalaryAmountLabel = (
  salaryType: string | undefined,
  t: TFunction,
): string => {
  if (salaryType === SalaryType.Monthly) {
    return t("Staff.AddStaffPanelFieldSalaryAmountMonthly");
  }
  if (salaryType === SalaryType.Hourly) {
    return t("Staff.AddStaffPanelFieldSalaryAmountHourly");
  }
  return t("Staff.AddStaffPanelFieldSalaryAmount");
};

interface FetchEmployeeNumberDeps {
  companyId: number;
  requestId: number;
  getCurrentRequestId: () => number;
  isMountedRef: MutableRefObject<boolean>;
  setField: <K extends keyof AddStaffFormData>(
    field: K,
    value: AddStaffFormData[K],
  ) => void;
  setIsLoadingEmployeeNumber: Dispatch<SetStateAction<boolean>>;
}

const fetchEmployeeNumberRequest = async (
  deps: FetchEmployeeNumberDeps,
): Promise<void> => {
  try {
    const response =
      await Api().company.organizationalStructure.getEmployeeNumber(
        deps.companyId,
      );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setField("employeeNumber", String(response.data ?? ""));
  } catch {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    // Preserve user-entered employee number on fetch failure.
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setIsLoadingEmployeeNumber(false);
  }
};

interface FetchEmploymentTemplatesDeps {
  companyId: number;
  requestId: number;
  getCurrentRequestId: () => number;
  isMountedRef: MutableRefObject<boolean>;
  setEmploymentTemplates: Dispatch<
    SetStateAction<EmploymentTemplateListItem[]>
  >;
  setIsLoadingEmploymentTemplates: Dispatch<SetStateAction<boolean>>;
}

const fetchEmploymentTemplatesRequest = async (
  deps: FetchEmploymentTemplatesDeps,
): Promise<void> => {
  try {
    const response = await Api().company.staff.getEmploymentTemplates(
      deps.companyId,
    );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setEmploymentTemplates(response.data ?? []);
  } catch {
    if (!deps.isMountedRef.current) return;
    // Keep existing templates on fetch failure.
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setIsLoadingEmploymentTemplates(false);
  }
};

interface FetchEmploymentTemplateDetailDeps {
  companyId: number;
  employmentTemplateId: number;
  requestId: number;
  getCurrentRequestId: () => number;
  isMountedRef: MutableRefObject<boolean>;
  setFormData: Dispatch<SetStateAction<AddStaffFormData>>;
  setTemplateSalaries: Dispatch<SetStateAction<EmploymentTemplateSalary[]>>;
}

const fetchEmploymentTemplateDetailRequest = async (
  deps: FetchEmploymentTemplateDetailDeps,
): Promise<void> => {
  try {
    const response = await Api().company.staff.getEmploymentTemplate(
      deps.companyId,
      deps.employmentTemplateId,
    );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    const template = response.data;
    if (template == null) return;
    deps.setFormData((previousFormData) =>
      applyEmploymentTemplateToAddStaffForm(previousFormData, template),
    );
    deps.setTemplateSalaries(template.salaries ?? []);
  } catch {
    if (!deps.isMountedRef.current) return;
    // Keep current form values when template detail fetch fails.
  }
};

interface LookupWorkerByPhoneDeps {
  companyId: number;
  phoneNumber: string;
  requestId: number;
  getCurrentRequestId: () => number;
  isMountedRef: MutableRefObject<boolean>;
  setFormData: Dispatch<SetStateAction<AddStaffFormData>>;
  setPhoneLookupState: Dispatch<SetStateAction<PhoneLookupState>>;
  translate: TFunction;
}

const lookupWorkerByPhoneRequest = async (
  deps: LookupWorkerByPhoneDeps,
): Promise<void> => {
  try {
    const response =
      await Api().company.organizationalStructure.getWorkerByNumber(
        deps.companyId,
        deps.phoneNumber,
      );

    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;

    const worker = response.data;
    if (!worker || worker.workerId === 0) {
      deps.setPhoneLookupState({ status: "verified" });
      return;
    }

    const hasEmploymentInfo =
      worker.employmentInfo !== null && worker.employmentInfo !== undefined;
    if (hasEmploymentInfo) {
      deps.setPhoneLookupState({
        status: "alreadyExists",
        message: deps.translate("Staff.AddStaffPanelWorkerAlreadyExists"),
      });
      return;
    }

    deps.setPhoneLookupState({ status: "verified" });
    deps.setFormData((prev) => ({
      ...prev,
      firstName: worker.firstName ?? "",
      lastName: worker.lastName ?? "",
      email: worker.email ?? "",
      personalIdentityNumber: worker.personalIdentityNumber ?? "",
      phoneNumber: (worker.phoneNumber ?? prev.phoneNumber)
        .replace(/\D/g, "")
        .slice(0, MAX_PHONE_DIGITS),
    }));
  } catch {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    // Keep manual entry when lookup fails.
  } finally {
    if (!deps.isMountedRef.current) return;
    deps.setPhoneLookupState((previousPhoneLookupState) => {
      if (previousPhoneLookupState.status === "checking") {
        return { status: "idle" };
      }
      return previousPhoneLookupState;
    });
  }
};

interface CreateWorkerDeps {
  companyId: number;
  formData: AddStaffFormData;
  requestId: number;
  payload: ReturnType<typeof mapAddStaffFormDataToWorkerPayload>;
  getCurrentRequestId: () => number;
  isMountedRef: MutableRefObject<boolean>;
  onWorkerCreated?: () => void;
  onClose: () => void;
  setSubmitError: Dispatch<SetStateAction<string>>;
  setSubmitState: Dispatch<SetStateAction<SubmitState>>;
  translate: TFunction;
}

const createWorkerRequest = async (deps: CreateWorkerDeps): Promise<void> => {
  await Api()
    .company.staff.createWorker(deps.companyId, deps.payload)
    .then(() => {
      if (!deps.isMountedRef.current) return;
      if (deps.getCurrentRequestId() !== deps.requestId) return;
      deps.onWorkerCreated?.();
      deps.onClose();
    })
    .catch((error: BackendAxiosError) => {
      if (!deps.isMountedRef.current) return;
      if (deps.getCurrentRequestId() !== deps.requestId) return;
      deps.setSubmitError(
        resolveCreateWorkerErrorMessage(error, deps.formData, deps.translate),
      );
    })
    .finally(() => {
      if (!deps.isMountedRef.current) return;
      if (deps.getCurrentRequestId() !== deps.requestId) return;
      deps.setSubmitState({ status: "idle" });
    });
};

/**
 * Encapsulates Add Staff panel form state, option mapping, validation and submit flow.
 */
export const useAddStaffPanelLogic = (
  props: UseAddStaffPanelLogicProps,
): AddStaffPanelLogicResult => {
  const translate = props.t;
  const isOpen = props.isOpen;
  const onClose = props.onClose;
  const onWorkerCreated = props.onWorkerCreated;
  const unitTeamOptions = props.unitTeamOptions;
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;
  const [formData, setFormData] = useState<AddStaffFormData>(
    createInitialFormData,
  );
  const [isLoadingEmployeeNumber, setIsLoadingEmployeeNumber] = useState(false);
  const [employmentTemplates, setEmploymentTemplates] = useState<
    EmploymentTemplateListItem[]
  >([]);
  const [isLoadingEmploymentTemplates, setIsLoadingEmploymentTemplates] =
    useState(false);
  const [selectedEmploymentTemplateId, setSelectedEmploymentTemplateId] =
    useState("");
  const [templateSalaries, setTemplateSalaries] = useState<
    EmploymentTemplateSalary[]
  >([]);
  const [selectedTemplateSalaryId, setSelectedTemplateSalaryId] = useState("");
  const [phoneLookupState, setPhoneLookupState] = useState<PhoneLookupState>({
    status: "idle",
  });
  const [submitState, setSubmitState] = useState<SubmitState>({
    status: "idle",
  });
  const [submitError, setSubmitError] = useState("");
  const [noticeDaysInput, setNoticeDaysInput] = useState(
    String(formData.noticeDays),
  );
  const [vacationDaysInput, setVacationDaysInput] = useState(
    String(formData.vacationDays),
  );
  const isMountedRef = useRef(true);
  const employeeNumberRequestIdRef = useRef(0);
  const createWorkerRequestIdRef = useRef(0);
  const phoneLookupRequestIdRef = useRef(0);
  const employmentTemplatesRequestIdRef = useRef(0);
  const employmentTemplateDetailRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setField = useCallback(
    <K extends keyof AddStaffFormData>(
      field: K,
      value: AddStaffFormData[K],
    ) => {
      setFormData((previousFormData) => ({
        ...previousFormData,
        [field]: value,
      }));
      setSubmitError("");
    },
    [],
  );

  useEffect(() => {
    setNoticeDaysInput(String(formData.noticeDays));
  }, [formData.noticeDays]);

  useEffect(() => {
    setVacationDaysInput(String(formData.vacationDays));
  }, [formData.vacationDays]);

  const createNumericFieldInputController = useCallback(
    (
      inputValue: string,
      setInputValue: Dispatch<SetStateAction<string>>,
      field: "noticeDays" | "vacationDays",
    ): NumericFieldInputController => {
      const onChange = (value: string): void => {
        setInputValue(value);
        if (value.trim().length === 0) return;
        const trimmedValue = value.trim();
        if (!/^\d+$/.test(trimmedValue)) return;
        const parsedValue = Number(trimmedValue);
        if (!Number.isSafeInteger(parsedValue)) return;
        setField(field, parsedValue);
      };

      const onBlur = (): void => {
        const trimmedValue = inputValue.trim();
        if (!/^\d+$/.test(trimmedValue)) {
          setInputValue("0");
          setField(field, 0);
          return;
        }
        const parsedValue = Number(trimmedValue);
        if (!Number.isSafeInteger(parsedValue)) {
          setInputValue("0");
          setField(field, 0);
          return;
        }
        setInputValue(String(parsedValue));
        setField(field, parsedValue);
      };

      return { value: inputValue, onChange, onBlur };
    },
    [setField],
  );

  const noticeDaysField = createNumericFieldInputController(
    noticeDaysInput,
    setNoticeDaysInput,
    "noticeDays",
  );

  const vacationDaysField = createNumericFieldInputController(
    vacationDaysInput,
    setVacationDaysInput,
    "vacationDays",
  );

  const onPhoneNumberChange = useCallback(
    (phoneNumber: string): void => {
      phoneLookupRequestIdRef.current += 1;
      setPhoneLookupState({ status: "idle" });
      setSubmitState({ status: "idle" });
      setSubmitError("");
      setField(
        "phoneNumber",
        phoneNumber.replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS),
      );
    },
    [setField],
  );

  const onPhoneNumberBlur = useCallback((): void => {
    if (!isValidPhoneNumber(formData.phoneNumber)) {
      if (formData.phoneNumber.trim().length > 0) {
        setPhoneLookupState({
          status: "invalid",
          message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
        });
      } else {
        setPhoneLookupState({ status: "idle" });
      }
      return;
    }

    if (phoneLookupState.status === "invalid") {
      setPhoneLookupState({ status: "idle" });
    }
  }, [formData.phoneNumber, phoneLookupState.status, translate]);

  const onPhoneNumberContinue = useCallback((): void => {
    if (!companyId) return;
    if (!isValidPhoneNumber(formData.phoneNumber)) {
      if (formData.phoneNumber.trim().length > 0) {
        setPhoneLookupState({
          status: "invalid",
          message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
        });
      } else {
        setPhoneLookupState({ status: "idle" });
      }
      return;
    }

    setSubmitState({ status: "idle" });
    setSubmitError("");
    setPhoneLookupState({ status: "checking" });
    const requestId = phoneLookupRequestIdRef.current + 1;
    phoneLookupRequestIdRef.current = requestId;
    void lookupWorkerByPhoneRequest({
      companyId,
      phoneNumber: formData.phoneNumber,
      requestId,
      getCurrentRequestId: () => phoneLookupRequestIdRef.current,
      isMountedRef,
      setFormData,
      setPhoneLookupState,
      translate,
    });
  }, [companyId, formData.phoneNumber, translate]);

  /**
   * Fetches latest employee number and ignores stale responses.
   */
  const fetchEmployeeNumber = useCallback((): void => {
    if (!companyId) return;
    const requestId = employeeNumberRequestIdRef.current + 1;
    employeeNumberRequestIdRef.current = requestId;
    setIsLoadingEmployeeNumber(true);
    void fetchEmployeeNumberRequest({
      companyId,
      requestId,
      getCurrentRequestId: () => employeeNumberRequestIdRef.current,
      isMountedRef,
      setField,
      setIsLoadingEmployeeNumber,
    });
  }, [companyId, setField]);

  /**
   * Fetches the available employment templates and ignores stale responses.
   */
  const fetchEmploymentTemplates = useCallback((): void => {
    if (!companyId) return;
    const requestId = employmentTemplatesRequestIdRef.current + 1;
    employmentTemplatesRequestIdRef.current = requestId;
    setIsLoadingEmploymentTemplates(true);
    void fetchEmploymentTemplatesRequest({
      companyId,
      requestId,
      getCurrentRequestId: () => employmentTemplatesRequestIdRef.current,
      isMountedRef,
      setEmploymentTemplates,
      setIsLoadingEmploymentTemplates,
    });
  }, [companyId]);

  /**
   * Applies the selected template onto the form and ignores stale responses.
   */
  const applyEmploymentTemplate = useCallback(
    (templateId: string): void => {
      setSelectedEmploymentTemplateId(templateId);
      setSelectedTemplateSalaryId("");
      setTemplateSalaries([]);
      if (!companyId || templateId.length === 0) return;
      const requestId = employmentTemplateDetailRequestIdRef.current + 1;
      employmentTemplateDetailRequestIdRef.current = requestId;
      void fetchEmploymentTemplateDetailRequest({
        companyId,
        employmentTemplateId: Number(templateId),
        requestId,
        getCurrentRequestId: () => employmentTemplateDetailRequestIdRef.current,
        isMountedRef,
        setFormData,
        setTemplateSalaries,
      });
    },
    [companyId],
  );

  /**
   * Prefills the salary amount from the selected template salary.
   */
  const applyEmploymentTemplateSalary = useCallback(
    (salaryId: string): void => {
      setSelectedTemplateSalaryId(salaryId);
      const selectedSalary = templateSalaries.find(
        (salary) => String(salary.id) === salaryId,
      );
      if (selectedSalary == null) return;
      setField("salaryAmount", String(selectedSalary.value));
    },
    [setField, templateSalaries],
  );

  useEffect(() => {
    createWorkerRequestIdRef.current += 1;
    employeeNumberRequestIdRef.current += 1;
    phoneLookupRequestIdRef.current += 1;
    employmentTemplatesRequestIdRef.current += 1;
    employmentTemplateDetailRequestIdRef.current += 1;

    if (!isOpen) return;
    setFormData(createInitialFormData());
    setNoticeDaysInput("0");
    setVacationDaysInput("0");
    setPhoneLookupState({ status: "idle" });
    setSubmitState({ status: "idle" });
    setSubmitError("");
    setSelectedEmploymentTemplateId("");
    setSelectedTemplateSalaryId("");
    setTemplateSalaries([]);
    fetchEmployeeNumber();
    fetchEmploymentTemplates();
  }, [fetchEmployeeNumber, fetchEmploymentTemplates, isOpen]);

  const subscription = useCompanySubscription({
    companyId,
    isEnabled: isOpen,
  });

  const teamOptions = useMemo(() => {
    return unitTeamOptions.flatMap((unit): SearchableDropdownOption[] => {
      const unitName = (unit.unitName ?? "").trim();
      return unit.teams.map((team) => {
        const teamName = (team.name ?? "").trim();
        return {
          value: String(team.id),
          label: unitName.length > 0 ? `${unitName} - ${teamName}` : teamName,
        };
      });
    });
  }, [unitTeamOptions]);

  const workerAge = useMemo(() => {
    return getAgeFromPersonalIdentityNumber(formData.personalIdentityNumber);
  }, [formData.personalIdentityNumber]);

  const visibleTemplateSalaries = useMemo(() => {
    return filterTemplateSalariesByAge(templateSalaries, workerAge);
  }, [templateSalaries, workerAge]);

  const employmentOptions = toEmploymentTypeOptions(translate);
  const salaryOptions = toTranslatedOptions(SALARY_OPTIONS, translate);
  const paymentOptions = toTranslatedOptions(PAYMENT_TYPE_OPTIONS, translate);
  const paymentMethodOptions = PAYMENT_METHOD_OPTIONS.map(
    (paymentMethodOption): AddStaffOption => ({
      value: paymentMethodOption.value,
      label: translate(paymentMethodOption.label),
    }),
  );
  const employeeCategoryOptions = toTranslatedOptions(
    EMPLOYEE_CATEGORY_OPTIONS,
    translate,
  );

  const salaryAmountLabel = useMemo(() => {
    return getSalaryAmountLabel(formData.salaryType, translate);
  }, [formData.salaryType, translate]);

  const isSimflexityEmployee = useMemo(() => {
    return (
      (formData.employmentType ?? "").toLowerCase() ===
      EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE.toLowerCase()
    );
  }, [formData.employmentType]);

  const isFlexCompany =
    subscription !== undefined &&
    subscription !== null &&
    subscription.includes("Flex");

  const hasSimflexityPay = useMemo(() => {
    const companySettings = companyState.company?.companySettings ?? [];
    return isSettingEnabled(companySettings, SIMFLEXITY_PAY_SETTING_KEY);
  }, [companyState.company?.companySettings]);

  const canSelectSimflexityPay = useMemo(() => {
    if (isSimflexityEmployee) return true;
    return (
      isFlexCompany &&
      hasSimflexityPay &&
      formData.salaryType === SalaryType.Hourly
    );
  }, [
    formData.salaryType,
    hasSimflexityPay,
    isFlexCompany,
    isSimflexityEmployee,
  ]);

  const validationRequirements = useMemo(() => {
    return getAddStaffValidationRequirements(formData);
  }, [formData]);

  const validationState = useMemo(() => {
    return getAddStaffValidationState(
      formData,
      validationRequirements,
      canSelectSimflexityPay,
    );
  }, [canSelectSimflexityPay, formData, validationRequirements]);

  const hasCheckedPhoneNumber = phoneLookupState.status === "verified";
  const isCheckingPhoneNumber = phoneLookupState.status === "checking";
  const phoneError =
    phoneLookupState.status === "invalid" ||
    phoneLookupState.status === "alreadyExists"
      ? phoneLookupState.message
      : "";
  const isSubmitting = submitState.status === "submitting";
  const paymentMethodError = getPaymentMethodError(
    formData,
    validationState.hasValidPaymentMethod,
    translate,
  );

  const areFormFieldsDisabled =
    formData.phoneNumber.trim().length === 0 || !hasCheckedPhoneNumber;
  const isCreateDisabled =
    areFormFieldsDisabled ||
    isSubmitting ||
    !validationState.hasRequiredPersonalValues ||
    !validationState.hasRequiredEmploymentValues;

  const onAddWorkerClick = useCallback((): void => {
    if (!companyId || isSubmitting) return;
    if (!validationState.hasRequiredPersonalValues) return;
    if (!validationState.hasRequiredEmploymentValues) return;
    if (!isValidPhoneNumber(formData.phoneNumber)) {
      setPhoneLookupState({
        status: "invalid",
        message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
      });
      return;
    }

    setSubmitError("");
    setSubmitState({ status: "submitting" });
    const requestId = createWorkerRequestIdRef.current + 1;
    createWorkerRequestIdRef.current = requestId;
    const payload = mapAddStaffFormDataToWorkerPayload(formData);
    void createWorkerRequest({
      companyId,
      formData,
      requestId,
      payload,
      getCurrentRequestId: () => createWorkerRequestIdRef.current,
      isMountedRef,
      onWorkerCreated,
      onClose,
      setSubmitError,
      setSubmitState,
      translate,
    });
  }, [
    companyId,
    formData,
    isSubmitting,
    onClose,
    onWorkerCreated,
    translate,
    validationState.hasRequiredPersonalValues,
    validationState.hasRequiredEmploymentValues,
  ]);

  return {
    formData,
    isLoadingEmployeeNumber,
    isCheckingPhoneNumber,
    isSubmitting,
    phoneError,
    submitError,
    teamOptions,
    employmentOptions,
    salaryOptions,
    paymentOptions,
    paymentMethodOptions,
    paymentMethodError,
    employeeCategoryOptions,
    salaryAmountLabel,
    areFormFieldsDisabled,
    isCreateDisabled,
    validationRequirements,
    validationState,
    employmentTemplates,
    isLoadingEmploymentTemplates,
    selectedEmploymentTemplateId,
    applyEmploymentTemplate,
    templateSalaries: visibleTemplateSalaries,
    selectedTemplateSalaryId,
    applyEmploymentTemplateSalary,
    fetchEmployeeNumber,
    setField,
    onPhoneNumberChange,
    onPhoneNumberBlur,
    onPhoneNumberContinue,
    noticeDaysInputController: noticeDaysField,
    vacationDaysInputController: vacationDaysField,
    onAddWorkerClick,
  };
};
